import React, { createContext, useState, useEffect, useCallback } from 'react'

import apiFetch from './axios/config'
import { toApiError } from './utils/apiError'

const EntityContext = createContext()

// Pedido de token, estado do token e sign: resolvem com o corpo ou rejeitam com toApiError.
const dataOrApiError = (request) =>
  request.then((response) => response.data, (error) => Promise.reject(toApiError(error)));

const isNoPlanActiveDetail = (detail) =>
  detail === 'No Plan Active' ||
  (typeof detail === 'string' && detail.toLowerCase().includes('no plan'));

const Entity = (props) => {

  // Inicializa entity do sessionStorage se disponível
  const getInitialEntity = () => {
    try {
      const savedEntity = sessionStorage.getItem('selectedEntity');
      return savedEntity ? JSON.parse(savedEntity) : '';
    } catch (error) {
      console.error('Error parsing saved entity:', error);
      return '';
    }
  };

  const [entity, setEntityState] = useState(getInitialEntity);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [signaturePosition, setSignaturePosition] = useState(null);
  const [entityHasActivePlan, setEntityHasActivePlan] = useState(null);
  const [signatureMethods, setSignatureMethods] = useState(null);
  const [entityPlanStatusLoading, setEntityPlanStatusLoading] = useState(false);
  const [planStatusNonce, setPlanStatusNonce] = useState(0);

  const refetchEntityPlanStatus = useCallback(() => {
    setPlanStatusNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!entity || typeof entity !== 'object' || !entity.api_key) {
      setEntityHasActivePlan(null);
      setEntityPlanStatusLoading(false);
      return () => { };
    }

    let cancelled = false;
    setEntityPlanStatusLoading(true);
    setEntityHasActivePlan(null);

    (async () => {
      try {
        const response = await apiFetch.get(`/entity`, {
          headers: { Authorization: `${entity.api_key}` },
        });
        if (!cancelled) {
          const methods = response.data?.signature_methods;
          setSignatureMethods(Array.isArray(methods) ? methods : null);
          setEntityHasActivePlan(true);
          setEntityPlanStatusLoading(false);
        }
      } catch (error) {
        if (cancelled) return;
        const detail = error.response?.data?.detail;
        if (isNoPlanActiveDetail(detail)) {
          setEntityHasActivePlan(false);
        } else {
          setEntityHasActivePlan(null);
          setErro(true);
          setErrorMessage(
            typeof detail === 'string' ? detail : 'Erro ao verificar organização'
          );
        }
        setEntityPlanStatusLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entity?.entity_key, entity?.api_key, planStatusNonce]);

  // Função para definir entidade e persistir no sessionStorage
  const setEntity = (newEntity) => {
    setEntityState(newEntity);
    if (newEntity && newEntity.api_key) {
      sessionStorage.setItem('selectedEntity', JSON.stringify(newEntity));
    } else {
      sessionStorage.removeItem('selectedEntity');
    }
  };

  // Função para validar se a entidade pertence ao usuário atual
  const validateEntity = async (token) => {
    if (!entity || typeof entity !== 'object' || !entity.api_key) {
      return false;
    }

    try {
      // Busca as entidades do usuário atual
      const userEntities = await getEntities(token);
      // Verifica se a entidade salva está na lista de entidades do usuário
      const entityBelongsToUser = userEntities.entities?.some(
        e => e.entity_key === entity.entity_key
      );

      if (!entityBelongsToUser) {
        // Se não pertence, limpa a entidade
        setEntityState('');
        sessionStorage.removeItem('selectedEntity');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error validating entity:', error);
      // Em caso de erro, limpa a entidade por segurança
      setEntityState('');
      sessionStorage.removeItem('selectedEntity');
      return false;
    }
  };

  const getEntities = async (token) => {
    return new Promise((resolve, reject) => {
      try {
        setLoading(true);
        apiFetch.get(
          `/user`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;

          setLoading(false);
          resolve(data);
        });
      } catch (error) {
        // Log the error for debugging purposes (optional)
        console.error('Error fetching:', error);
        setLoading(false);
        reject(error);
      }
    });
  };

  const getEntity = async () => {
    try {
      setLoading(true);
      const response = await apiFetch.get(
        `/entity`,
        { headers: { Authorization: `${entity.api_key}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      setEntityHasActivePlan(true);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      const detail = error.response?.data?.detail;
      if (isNoPlanActiveDetail(detail)) {
        setEntityHasActivePlan(false);
      } else {
        setErro(true);
        setErrorMessage(detail);
      }
      return { error };
    }
  };

  const setEntityPlan = async (token, planKey) => {

    const payload = {
      "entity_key": entity.entity_key,
      "plan_id": planKey
    }

    try {
      setLoading(true);
      const response = await apiFetch.patch(
        `/entity/plan`, payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      setErro(false);
      setEntityHasActivePlan(true);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const addUserToEntity = async (token, userDocumentNumber, roleType) => {

    const payload = {
      "entity_key": entity.entity_key,
      "user_document_number": userDocumentNumber,
      "role_type": roleType
    }

    try {
      setLoading(true);
      const response = await apiFetch.post(
        `/entity/user`, payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const RemoveUserFromEntity = async (token, userKey) => {

    try {
      setLoading(true);
      const response = await apiFetch.delete(
        `/entity/user?entity_key=${entity.entity_key}&user_key=${userKey}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const deleteGroup = async (groupKey) =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true)

        apiFetch.delete(
          `/group?group_key=${groupKey}`,
          {
            headers: { 'Authorization': `${entity.api_key}` }
          }
        )
          .then((response) => {
            setLoading(false)
            resolve(response.data)
          })
          .catch((error) => {
            setLoading(false)
            reject(error)
          })

      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const DeleteSigner = async (signerKey) => {

    try {
      setLoading(true);
      const response = await apiFetch.delete(
        `/signer?signer_key=${signerKey}`,
        { headers: { Authorization: entity.api_key } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const getPlan = async () => {
    try {
      setLoading(true);
      const response = await apiFetch.get(
        `/plan`,
        { headers: { Authorization: `${entity.api_key}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const getCheckout = async () => {
    try {
      setLoading(true);
      const response = await apiFetch.get(
        `/checkout`,
        { headers: { Authorization: `${entity.api_key}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const getinvoices = async () => {
    try {
      setLoading(true);
      const response = await apiFetch.get(
        `/checkout/invoices`,
        { headers: { Authorization: `${entity.api_key}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const getSpecificPlan = async (id) => {
    try {
      setLoading(true);
      const response = await apiFetch.get(
        `/plan?id=${id}`,
        { headers: { Authorization: `${entity.api_key}` } },
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };


  const getEnvelopeList = async (page = 1) => {
    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/envelope?page=${page}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const getEndorsementsList = async (page = 1, endorsementKey = null) => {
    try {
      setLoading(true)
      const url = endorsementKey
        ? `/endorsement?endorsement_key=${encodeURIComponent(endorsementKey)}`
        : `/endorsement?page=${page}`;
      const response = await apiFetch.get(
        url, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        if (endorsementKey) {
          return { endorsements: [], total_results: 0 };
        }
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      if (endorsementKey) {
        if (data?.endorsements) {
          return data;
        }
        if (data?.envelope_key) {
          return { endorsements: [data], total_results: 1 };
        }
        return { endorsements: [], total_results: 0 };
      }
      return data;
    } catch (error) {
      console.error('Error fetching:', error);
      setLoading(false);
      if (endorsementKey) {
        return { endorsements: [], total_results: 0 };
      }
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const getBatchEndorsementsList = async (batchEndorsementKey = null) => {
    try {
      setLoading(true)
      const url = batchEndorsementKey
        ? `/endorsement/batch?batch_endorsement_key=${encodeURIComponent(batchEndorsementKey)}`
        : `/endorsement/batch`;
      const response = await apiFetch.get(
        url, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        if (batchEndorsementKey) {
          return { batch_endorsements: [] };
        }
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      if (batchEndorsementKey) {
        if (data?.batch_endorsements) {
          return data;
        }
        if (data?.batch_envelope_key || data?.batch_endorsement_key) {
          return { batch_endorsements: [data] };
        }
        return { batch_endorsements: [] };
      }
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      if (batchEndorsementKey) {
        return { batch_endorsements: [] };
      }
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const getPendingSignatures = async (token, page = 1, signatureType = null) => {
    try {
      setLoading(true);
      let url = `/user/pending-signatures?page=${page}`;
      if (signatureType) {
        url += `&signature_type=${signatureType}`;
      }

      const response = await apiFetch.get(
        url,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true);
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error fetching pending signatures:', error);
      setLoading(false);
      setErro(true);

      // Tratar diferentes tipos de erro
      let errorMessage = 'Erro ao buscar assinaturas pendentes';
      if (error.response?.status === 422) {
        // Erro de validação - pode ser que o signature_type não seja suportado
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail?.message) {
          errorMessage = detail.message;
        } else {
          errorMessage = 'Tipo de assinatura não suportado ou inválido';
        }
      } else if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : 'Erro ao buscar assinaturas pendentes';
      }

      setErrorMessage(errorMessage);
      // Retornar um objeto simples de erro, não o objeto completo
      return { error: true, message: errorMessage };
    }
  };

  const getPendingBatchSignatures = async (token, page = 1) => {
    try {
      setLoading(true);
      const response = await apiFetch.get(
        `/user/pending-batch-signatures?page=${page}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true);
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error fetching pending batch signatures:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response?.data?.detail || 'Erro ao buscar assinaturas em lote pendentes');
      return { error };
    }
  };

  // summary: só a linha do lote e os contadores, sem envelopes nem grupos (CHAN-123)
  const getBatchEndorsementDetails = async (batchEndorsementKey, { limit = 500, offset = 0, summary = false } = {}) => {
    return new Promise((resolve, reject) => {
      try {
        setLoading(true);
        const params = new URLSearchParams(
          summary
            ? { batch_endorsement_key: batchEndorsementKey, summary: 'true' }
            : { batch_endorsement_key: batchEndorsementKey, limit: String(limit), offset: String(offset) }
        );
        const url = `/endorsement/batch?${params.toString()}`;
        apiFetch.get(
          url,
          { headers: { Authorization: `${entity.api_key}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;

          setLoading(false);
          resolve(data);
        });
      } catch (error) {
        console.error('Error fetching:', error);
        setLoading(false);
        reject(error);
      }
    });
  }

  const finalizeBatchEndorsement = async (batchEndorsementKey) => {
    return new Promise((resolve, reject) => {
      try {
        setLoading(true);
        const url = `/endorsement/batch/${batchEndorsementKey}/finalize`;
        apiFetch.post(
          url,
          {},
          { headers: { Authorization: `${entity.api_key}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;

          setLoading(false);
          resolve(data);
        });
      } catch (error) {
        console.error('Error finalizing batch endorsement:', error);
        setLoading(false);
        reject(error);
      }
    });
  }

  // Sem o overlay global: a página repete esta chamada enquanto o ZIP é gerado e mostra o progresso no botão.
  const downloadBatchEndorsementZip = async (batchEndorsementKey, fileType) => {
    return new Promise((resolve, reject) => {
      try {
        const url = `/endorsement/batch/${batchEndorsementKey}/download`;
        apiFetch.post(
          url,
          { file_type: fileType },
          { headers: { Authorization: `${entity.api_key}` } }
        ).then((response) => {
          if (response.status === 404) {
            reject(new Error('Not Found'));
            return;
          }

          resolve(response.data);
        }).catch((error) => {
          // Handle 409 Conflict specifically
          if (error.response && error.response.status === 409) {
            const conflictError = new Error(error.response.data?.detail || 'Processamento já em andamento');
            conflictError.status = 409;
            reject(conflictError);
          } else {
            reject(error);
          }
        });
      } catch (error) {
        console.error('Error downloading batch endorsement ZIP:', error);
        reject(error);
      }
    });
  }

  const cancelBatchEndorsement = async (batchEndorsementKey) =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true);
        const url = `/endorsement/batch/${batchEndorsementKey}`;
        apiFetch
          .delete(url, { headers: { Authorization: `${entity.api_key}` } })
          .then((response) => {
            setLoading(false);
            resolve(response.data);
          })
          .catch((error) => {
            setLoading(false);
            setErro(true);
            setErrorMessage(
              error.response?.data?.detail || 'Erro ao cancelar o lote de endosso'
            );
            reject(error);
          });
      } catch (error) {
        console.error('Error canceling batch endorsement:', error);
        setLoading(false);
        reject(error);
      }
    });

  const getXpressSignaturesList = async () => {
    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/signature/get/xpress`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const createEnvelope = async (payload) => {
    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/envelope`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      if (response.status === 400) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Error') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const getEnvelope = async (envelopeKey) => {
    if (!envelopeKey) {
      setErro(true)
      return { error: new Error('Missing Params') };
    }
    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/envelope?envelope_key=${envelopeKey}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const getTemplates = async () => {
    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/template`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const getTemplate = async (templateKey) => {
    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/template?template_key=${templateKey}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      )
      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const deleteTemplate = async (templateKey) => {
    new Promise((resolve, reject) => {
      try {
        setLoading(true)
        apiFetch.delete(
          `/template?template_key=${templateKey}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;

          setLoading(false);
          resolve(data);
        });
      } catch (error) {
        // Log the error for debugging purposes (optional)
        console.error('Error fetching:', error);
        setLoading(false);
        reject(error);
      }
    });
  };

  const vinculateDocumentToEnvelope = async (documentKey, envelopeKey) =>
    new Promise((resolve, reject) => {

      const payload = {
        "envelope_key": envelopeKey,
        "document_key": documentKey
      }

      try {
        setLoading(true)
        apiFetch.post(
          `/envelope/document`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false)
          resolve(true)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }

    })

  const removeDocumentToEnvelope = async (documentKey, envelopeKey) =>
    new Promise((resolve, reject) => {

      const payload = {
        "envelope_key": envelopeKey,
        "document_key": documentKey
      }

      try {
        setLoading(true)
        apiFetch.delete(
          `/envelope/document`, { data: payload, headers: { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false)
          resolve(true)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }

    })

  const createDocument = async (name, base64) =>
    new Promise((resolve, reject) => {

      const formatedName = name.replace(/\.[^/.]+$/, "")

      const payload = {
        "document_name": formatedName,
        "base64": base64
      }

      try {
        setLoading(true)
        apiFetch.post(
          `/document`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;

          const documentKey = data.document_key

          const newFile = {
            "documentKey": documentKey,
            "documentName": formatedName
          }
          setLoading(false)
          resolve(newFile)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const createDocumentByTemplate = async (documentName, templateKey, variables) =>
    new Promise((resolve, reject) => {
      const payload = {
        document_name: documentName,
        template_key: templateKey,
        variables,
      };

      try {
        setLoading(true);
        apiFetch.post(
          `/document/template`, payload, { headers: { Authorization: `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false);
          resolve(data);
        }).catch((error) => {
          setLoading(false);
          reject(error);
        });
      } catch (error) {
        setLoading(false);
        reject(error);
      }
    })

  const createXpressEnvelopeByTemplate = async (payload) =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true);
        apiFetch.post(
          `/xpress/envelope/template`, payload, { headers: { Authorization: `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false);
          resolve(data);
        }).catch((error) => {
          setLoading(false);
          reject(error);
        });
      } catch (error) {
        setLoading(false);
        reject(error);
      }
    })

  const createTemplate = async (name, base64, templateType) =>
    new Promise((resolve, reject) => {

      const formatedName = name.replace(/\.[^/.]+$/, "")

      const payload = {
        "template_name": formatedName,
        "base64": base64,
        "template_type": templateType
      }

      try {
        setLoading(true)
        apiFetch.post(
          `/template`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;

          const documentKey = data.template_key

          const newFile = {
            "templateKey": documentKey,
            "documentName": formatedName
          }
          setLoading(false)
          resolve(newFile)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const createBatchSignature = async (token, envelopeSignerKeyList) => {

    const payload = {
      "envelope_signer_key": envelopeSignerKeyList
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/signature/batch`, payload, { 'headers': { 'Authorization': `Bearer ${token}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const requestBatchSignatureToken = async (token, batchSignatureKey) => {

    const payload = {
      "signature_batch_key": batchSignatureKey
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/signature/batch/token`, payload, { 'headers': { 'Authorization': `Bearer ${token}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error requesting batch signature token:', error.message);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  };

  const getBatchSignatureDetails = async (token, batchEnvelopeSignerKey) => {
    try {
      setLoading(true)
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await apiFetch.get(
        `/signature/batch/${batchEnvelopeSignerKey}`, { headers }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response?.data?.detail || error.message)
      return { error };
    }
  };

  const createWebhook = async (url, header) =>
    new Promise((resolve, reject) => {

      const headerJson = JSON.parse(header);

      const payload = {
        "url": url,
        "headers": headerJson
      }

      try {
        setLoading(true)
        apiFetch.post(
          `/webhook`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false)
          resolve(data)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const deleteWebhook = async (webhookConfigurationKey) =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true)
        apiFetch.delete(
          `/webhook?webhook_configuration_key=${webhookConfigurationKey}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;

          setLoading(false)
          resolve(data)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const getWebhooks = async () =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true)
        apiFetch.get(
          `/webhook`, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;

          setLoading(false)
          resolve(data)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const getSigners = async () =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true)
        apiFetch.get(
          `/signer`, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;

          setLoading(false);
          resolve(data);
        });
      } catch (error) {
        // Log the error for debugging purposes (optional)
        console.error('Error fetching:', error);
        setLoading(false);
        reject(error);
      }
    });

  const getSignerByDocument = async (signerDocumentNumber) =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true)
        apiFetch.get(
          `/signer?signer_document_number=${signerDocumentNumber}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;

          setLoading(false);
          resolve(data);
        });
      } catch (error) {
        // Log the error for debugging purposes (optional)
        console.error('Error fetching:', error);
        setLoading(false);
        reject(error);
      }
    });

  const createSigner = async (name, email, documentNumber, phoneNumber) => {

    const payload = {
      "signer_email": email,
      "signer_name": name,
      "signer_document_number": documentNumber,
      ...(phoneNumber ? { "phone_number": phoneNumber } : {})
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/signer`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      )
      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;
      const signerKey = data.signer_key

      const newSigner = {
        "signerKey": signerKey,
        "signerName": name,
        "signerDocumentNumber": documentNumber
      }


      setLoading(false);
      return newSigner;

    } catch (error) {
      const apiError = toApiError(error);
      setLoading(false);
      setErro(true);
      setErrorMessage(apiError.message)
      return { error: apiError };
    }
  }

  const getSigner = async (signerKey) => {
    try {
      setLoading(true);
      const response = await apiFetch.get(`/signer`, {
        headers: { Authorization: `${entity.api_key}` },
        params: { signer_key: signerKey },
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      return { error: toApiError(error) };
    }
  };

  // PATCH /signer aceita só {signer_key, phone_number}; null remove o celular do cadastro
  const editSigner = async (signerKey, phoneNumber) => {
    try {
      setLoading(true);
      const response = await apiFetch.patch(
        `/signer`,
        { signer_key: signerKey, phone_number: phoneNumber || null },
        { headers: { Authorization: `${entity.api_key}` } }
      );
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      return { error: toApiError(error) };
    }
  };

  const getGroups = async () => {

    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/group`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      )
      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;

    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data[0].detail[0].msg)
      return { error };
    }
  }

  const getGroup = async (groupKey) => {

    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/group?group_key=${groupKey}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      )
      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;

    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data[0].detail[0].msg)
      return { error };
    }
  }

  const getEnvelopeGroup = async (envelopeGroupKey) => {

    try {
      setLoading(true)
      const response = await apiFetch.get(
        `envelope-group?envelope_group_key=${envelopeGroupKey}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      )
      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;

    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data[0].detail[0].msg)
      return { error };
    }
  }

  const getGroupByName = async (groupName) => {
    try {
      setLoading(true)
      const response = await apiFetch.get(
        `/group?group_name=${groupName}`, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }


  const createGroup = async (groupName) => {

    const payload = {
      "group_name": groupName
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/group`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      const groupKey = data.group_key

      const newGroup = {
        "groupKey": groupKey,
        "groupName": groupName
      }


      setLoading(false);
      return newGroup;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const resendEmail = async (envelopeSignerKey) => {

    const payload = {}

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/signature/send/${envelopeSignerKey}`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;


      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const editGroupApi = async (groupKey, signersMinimum, groupName) => {

    const payload = {
      group_key: groupKey,
      ...(groupName != null && groupName !== '' && { group_name: groupName }),
      ...(signersMinimum !== "0" && signersMinimum !== 0 && { signers_minimum: signersMinimum })
    };

    try {
      setLoading(true)
      const response = await apiFetch.patch(
        `/group`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }
      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const RemoveGroupApi = async (envelopeKey, envelopeGroupKey) => {

    const payload = {
      "envelope_key": "string",
      "envelope_group_key": "string"
    }

    try {
      setLoading(true)
      const response = await apiFetch.patch(
        `/group`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }
      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const VinculateGroupToEnvelope = async (envelopeKey, groupKey, signatureType) => {

    const payload = {
      "envelope_key": envelopeKey,
      "signers": [
        {
          "group_key": groupKey,
          "signature_type": signatureType
        }
      ]
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/envelope/group`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      if (response.status === 400) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Error') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      const apiError = toApiError(error);
      setLoading(false);
      setErro(true);
      setErrorMessage(apiError.message)
      return { error: apiError };
    }

  }

  const vinculateSignerToGroup = async (groupKey, signerKey, roleType, required) => {

    const payload = {
      "signer_key": signerKey,
      "group_key": groupKey,
      "role_type": roleType,
      "required": required
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/group/signer`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        return { error: new Error('Not Found') };
      }

      const data = response.data;


      setLoading(false);
      return data;
    } catch (error) {
      // Log the error for debugging purposes (optional)
      console.error('Error fetching:', error);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return { error };
    }
  }

  const DeleteGroupFromEnvelope = async (envelopeKey, groupKey) =>
    new Promise((resolve, reject) => {

      const payload = {
        "envelope_key": envelopeKey,
        "envelope_group_key": groupKey
      }

      try {
        setLoading(true)
        apiFetch.delete(
          `/envelope/group`, { data: payload, headers: { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false)
          resolve(data)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const deleteSignerFromGroup = async (groupKey, signerKey) =>
    new Promise((resolve, reject) => {

      const payload = {
        "signer_key": signerKey,
        "group_key": groupKey
      }

      try {
        setLoading(true)
        apiFetch.delete(
          `/group/signer`, { data: payload, headers: { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false)
          resolve(data)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  const sendToSignature = async (envelopeKey) =>
    new Promise((resolve, reject) => {

      const payload = {}

      try {
        setLoading(true)
        apiFetch.post(
          `/signature/send?envelope_key=${envelopeKey}`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
        ).then((response) => {
          const data = response.data;
          setLoading(false)
          resolve(data)
        })
      } catch (error) {
        setLoading(false)
        reject(error)
      }
    })

  // Função centralizada para gerar evidence padronizada
  const generateEvidence = (ipData, fingerprint) => {
    // Extrair apenas a string do IP do objeto JSON (se for objeto) ou usar diretamente (se for string)
    const ipString = typeof ipData === 'object' && ipData?.query ? ipData.query : ipData;

    return {
      "ip": ipString,
      "fingerprint": fingerprint
    };
  };

  const withLoading = (promise) => {
    setLoading(true)
    return promise.finally(() => setLoading(false))
  }

  const sendSignature = (otp, signerKey, ipData, fingerprint) => {
    const payload = {
      "token": otp,
      "envelope_signer_key": signerKey,
      "evidence": generateEvidence(ipData, fingerprint)
    }
    return withLoading(dataOrApiError(apiFetch.post(
      `/signature/sign`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
    )))
  }

  const sendIcpSignature = async (otp, signerKey, signature) => {

    const payload = {
      "token": otp,
      "envelope_signer_key": signerKey,
      "evidence": {
        "signature": signature
      }
    }

    try {
      setLoading(true)
      const response = await apiFetch.post(
        `/signature/sign`, payload, { 'headers': { 'Authorization': `${entity.api_key}` } }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        setErrorMessage(response.data.detail)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error sending ICP signature:', error.message);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response.data.detail)
      return null;
    }
  }

  // Nova função para gerar tokens ICP em lote (assinatura em lote)
  const requestBatchEnvelopeToken = async (batchEnvelopeSignerKey, certificate, bearerToken) => {
    const payload = {
      "batch_envelope_signer_key": batchEnvelopeSignerKey,
      "certificate": certificate
    }

    try {
      setLoading(true)
      const headers = bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
      const response = await apiFetch.post(
        `/signature/batch/token`, payload, { headers }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        setErrorMessage(response.data.detail)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error requesting batch envelope token:', error.message);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response?.data?.detail || 'Erro ao gerar tokens em lote')
      return null;
    }
  }

  // Nova função para finalizar assinaturas ICP em lote (assinatura em lote)
  const sendBatchIcpSignature = async (batchEnvelopeSignerKey, evidence, bearerToken) => {
    const payload = {
      "batch_envelope_signer_key": batchEnvelopeSignerKey,
      "evidence": evidence
    }

    try {
      setLoading(true)
      const headers = bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
      const response = await apiFetch.post(
        `/signature/batch/sign`, payload, { headers }
      );

      if (response.status === 404) {
        setLoading(false);
        setErro(true)
        setErrorMessage(response.data.detail)
        return { error: new Error('Not Found') };
      }

      const data = response.data;

      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error sending batch ICP signature:', error.message);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response?.data?.detail || 'Erro ao finalizar assinaturas em lote')
      return null;
    }
  }

  const requestBatchEndorsementIcpToken = async (batchEnvelopeSignerKey, certificate, bearerToken) => {
    const payload = {
      batch_envelope_signer_key: batchEnvelopeSignerKey,
      certificate,
    };
    try {
      setLoading(true);
      const headers = bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
      const response = await apiFetch.post(
        `/endorsement/batch/icp/token`,
        payload,
        { headers }
      );
      if (response.status === 404) {
        setLoading(false);
        setErro(true);
        setErrorMessage(response.data.detail);
        return { error: new Error('Not Found') };
      }
      const data = response.data;
      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error requesting batch endorsement ICP token:', error.message);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response?.data?.detail || 'Erro ao gerar tokens ICP (endosso)');
      return null;
    }
  };

  const sendBatchEndorsementIcpSignature = async (batchEnvelopeSignerKey, signatures, bearerToken) => {
    const payload = {
      batch_envelope_signer_key: batchEnvelopeSignerKey,
      signatures,
    };
    try {
      setLoading(true);
      const headers = bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
      const response = await apiFetch.post(
        `/endorsement/batch/icp/sign`,
        payload,
        { headers }
      );
      if (response.status === 404) {
        setLoading(false);
        setErro(true);
        setErrorMessage(response.data.detail);
        return { error: new Error('Not Found') };
      }
      const data = response.data;
      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error sending batch endorsement ICP signature:', error.message);
      setLoading(false);
      setErro(true);
      setErrorMessage(error.response?.data?.detail || 'Erro ao finalizar assinaturas ICP (endosso)');
      return null;
    }
  };

  // Pedido de token OTP do lote de endosso (e-mail ou WhatsApp)
  const requestBatchEndorsementToken = (batchEnvelopeSignerKey) =>
    withLoading(dataOrApiError(apiFetch.post(
      `/endorsement/batch/token`, { "batch_envelope_signer_key": batchEnvelopeSignerKey }
    )))

  // Assinatura do lote de endosso com token OTP (e-mail ou WhatsApp)
  const sendBatchEndorsementSignature = (batchEnvelopeSignerKey, token, ipData, fingerprint) => {
    const payload = {
      "token": token,
      "batch_envelope_signer_key": batchEnvelopeSignerKey,
      "evidence": generateEvidence(ipData, fingerprint)
    }
    return withLoading(dataOrApiError(apiFetch.post(`/endorsement/batch/sign`, payload)))
  }

  // Assinatura do lote do usuário logado
  const sendBatchSignature = (token, otp, batchKey, ipData, fingerprint) => {
    const payload = {
      "token": otp,
      "signature_batch_key": batchKey,
      "evidence": generateEvidence(ipData, fingerprint)
    }
    return withLoading(dataOrApiError(apiFetch.post(
      `/signature/batch/sign`, payload, { 'headers': { 'Authorization': `Bearer ${token}` } }
    )))
  }

  const requestSignatureToken = (payload) =>
    withLoading(dataOrApiError(apiFetch.post(`/signature/token`, payload)))

  // Estado do token, sem envio (recarga da tela e consulta periódica): não liga o loading global
  const getSignatureTokenState = (envelopeSignerKey) =>
    dataOrApiError(apiFetch.get(`/signature/${envelopeSignerKey}/token`))

  const getBatchSignatureTokenState = (batchEnvelopeSignerKey) =>
    dataOrApiError(apiFetch.get(`/signature/batch/${batchEnvelopeSignerKey}/token`))

  const deleteEnvelope = async (envelipeKey) =>
    new Promise((resolve, reject) => {
      try {
        setLoading(true);
        apiFetch
          .delete(`/envelope?envelope_key=${envelipeKey}`, {
            headers: { Authorization: `${entity.api_key}` },
          })
          .then((response) => {
            const data = response.data;
            setLoading(false);
            resolve(data);
          })
          .catch((error) => {
            setLoading(false);
            reject(error);
          });
      } catch (error) {
        setLoading(false);
        reject(error);
      }
    });

  const getMyEnvelopes = async (token, page = 1, maxResults = 20, status = null) => {
    return new Promise((resolve, reject) => {
      try {
        setLoading(true);
        let url = `/my/envelopes?page=${page}&max_results=${maxResults}`;
        if (status) {
          url += `&status=${status}`;
        }

        apiFetch.get(
          url,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;
          setLoading(false);
          resolve(data);
        }).catch((error) => {
          setLoading(false);
          setErro(true);
          setErrorMessage(error.response?.data?.detail || 'Erro ao buscar envelopes');
          reject(error);
        });
      } catch (error) {
        console.error('Error fetching my envelopes:', error);
        setLoading(false);
        reject(error);
      }
    });
  };

  const getMyEnvelopeDetails = async (token, envelopeKey) => {
    return new Promise((resolve, reject) => {
      try {
        setLoading(true);
        apiFetch.get(
          `/my/envelopes/${envelopeKey}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then((response) => {
          if (response.status === 404) {
            setLoading(false);
            reject(new Error('Not Found'));
            return;
          }

          const data = response.data;
          setLoading(false);
          resolve(data);
        }).catch((error) => {
          setLoading(false);
          setErro(true);
          setErrorMessage(error.response?.data?.detail || 'Erro ao buscar detalhes do envelope');
          reject(error);
        });
      } catch (error) {
        console.error('Error fetching envelope details:', error);
        setLoading(false);
        reject(error);
      }
    });
  };

  return (
    <EntityContext.Provider value={{
      getEntities
      , loading
      , entity
      , setEntity
      , getEntity
      , validateEntity
      , getEnvelopeList
      , setEntityPlan
      , addUserToEntity
      , RemoveUserFromEntity
      , createEnvelope
      , getEnvelope
      , createDocument
      , createDocumentByTemplate
      , createXpressEnvelopeByTemplate
      , vinculateDocumentToEnvelope
      , removeDocumentToEnvelope
      , getTemplates
      , getTemplate
      , deleteTemplate
      , createTemplate
      , getWebhooks
      , createWebhook
      , deleteWebhook
      , getSigners
      , DeleteSigner
      , deleteGroup
      , getSignerByDocument
      , createSigner
      , getSigner
      , editSigner
      , getGroups
      , getGroup
      , getGroupByName
      , getEnvelopeGroup
      , editGroupApi
      , createGroup
      , VinculateGroupToEnvelope
      , vinculateSignerToGroup
      , deleteSignerFromGroup
      , sendToSignature
      , sendSignature
      , sendBatchSignature
      , requestSignatureToken
      , getSignatureTokenState
      , getBatchSignatureTokenState
      , deleteEnvelope
      , DeleteGroupFromEnvelope
      , createBatchSignature
      , requestBatchSignatureToken
      , getBatchSignatureDetails
      , getPlan
      , getXpressSignaturesList
      , getSpecificPlan
      , getCheckout
      , getinvoices
      , resendEmail
      , sendIcpSignature
      , requestBatchEnvelopeToken
      , sendBatchIcpSignature
      , requestBatchEndorsementIcpToken
      , sendBatchEndorsementIcpSignature
      , requestBatchEndorsementToken
      , sendBatchEndorsementSignature
      , getEndorsementsList
      , getBatchEndorsementsList
      , getBatchEndorsementDetails
      , finalizeBatchEndorsement
      , downloadBatchEndorsementZip
      , cancelBatchEndorsement
      , getPendingSignatures
      , getPendingBatchSignatures
      , getMyEnvelopes
      , getMyEnvelopeDetails
      , signaturePosition
      , setSignaturePosition
      , erro
      , setErro
      , errorMessage
      , entityHasActivePlan
      , signatureMethods
      , entityPlanStatusLoading
      , refetchEntityPlanStatus
    }}>
      {props.children}
    </EntityContext.Provider>
  )

}

export { Entity, EntityContext }